// Frontend mirror of the provider-neutral wallet actions/locations model.
//
// The canonical definition + provider mapping lives in
// amplify/functions/common/walletActions.ts. The frontend can't import from
// amplify/ (Create React App's ModuleScopePlugin blocks out-of-src imports at
// build time), so the type shape and a few display helpers are duplicated here.
// Keep the two in sync when the model changes.

export type WalletProvider = "apple" | "google" | "both";

export interface WalletAction {
  id: string;
  provider: WalletProvider;
  label: string;
  url: string;
  actionType?: string;
  priority?: number;
  enabled: boolean;
  startsAt?: string;
  endsAt?: string;
}

export interface WalletLocation {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  address?: string;
  googlePlaceId?: string;
  provider: WalletProvider;
  enabled: boolean;
}

// The demo action presets from the spec. `actionType` drives Apple's fixed
// category mapping on the backend; `label`/`url` are merchant-editable.
export const ACTION_PRESETS: Array<{ actionType: string; label: string }> = [
  { actionType: "shop_now", label: "Shop now" },
  { actionType: "view_balance", label: "View balance" },
  { actionType: "find_store", label: "Find nearest store" },
  { actionType: "redeem_online", label: "Redeem online" },
  { actionType: "book_appointment", label: "Book appointment" },
  { actionType: "view_offer", label: "View offer" },
  { actionType: "contact_support", label: "Contact support" },
  { actionType: "track_order", label: "Track order" },
  { actionType: "open_app", label: "Open app" },
  { actionType: "manage_preferences", label: "Manage preferences" },
];

// Mirror of PROVIDER_LIMITS in the backend module (verified against Google's
// API: merchantLocations hard-capped at 10).
export const PROVIDER_LIMITS = {
  google: { maxLocations: 10, maxLinks: 10 },
  apple: { maxLocations: 10, maxFeaturedActions: 2 },
} as const;

export const APPLE_FEATURED_ACTIONS_NOTE =
  "Apple Featured Actions are implemented for demo/future support and should be " +
  "enabled publicly once Apple availability is confirmed.";

/** Mirrors the backend selector — for live preview of what each pass will show. */
export function isActionActive(action: WalletAction, nowMs: number = Date.now()): boolean {
  if (!action || action.enabled === false) return false;
  if (!action.url || !action.label) return false;
  if (action.startsAt) {
    const s = Date.parse(action.startsAt);
    if (!Number.isNaN(s) && nowMs < s) return false;
  }
  if (action.endsAt) {
    const e = Date.parse(action.endsAt);
    if (!Number.isNaN(e) && nowMs > e) return false;
  }
  return true;
}
